import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import AdminNavbar from "../components/AdminNavbar";
import "./AdminOrderDetails.css";

const getStatusConfig = (status) => {
  const configs = {
    pending: { color: "#d97706", bg: "#fef3c7", label: "Pending" },
    processing: { color: "#2563eb", bg: "#dbeafe", label: "Processing" },
    shipped: { color: "#4f46e5", bg: "#e0e7ff", label: "Shipped" },
    delivered: { color: "#059669", bg: "#d1fae5", label: "Delivered" },
    cancelled: { color: "#dc2626", bg: "#fee2e2", label: "Cancelled" },
  };
  return (
    configs[status] || {
      color: "#6b7280",
      bg: "#f3f4f6",
      label: status || "Unknown",
    }
  );
};

const getItemImageUrl = (item) => {
  const directImage = typeof item?.image === "string" ? item.image.trim() : "";
  if (directImage && directImage !== "[object Object]") {
    return directImage;
  }

  const images = Array.isArray(item?.product?.images)
    ? item.product.images
    : [];
  if (images.length > 0) {
    const first = [...images].sort((a, b) => {
      const aOrder =
        typeof a === "object" && a !== null ? Number(a.order ?? 0) : 0;
      const bOrder =
        typeof b === "object" && b !== null ? Number(b.order ?? 0) : 0;
      return aOrder - bOrder;
    })[0];

    if (typeof first === "string") {
      return first;
    }

    if (first && typeof first === "object" && typeof first.url === "string") {
      return first.url;
    }
  }

  if (typeof item?.product?.image === "string") {
    return item.product.image;
  }

  return "";
};

export default function AdminOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.title = "Admin Order Details | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Review order details on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const response = await api.getAdminOrder(id);

        if (!response.success || !response.data) {
          alert(response.message || "Order not found");
          navigate("/admin/orders");
          return;
        }

        setOrder(response.data);
      } catch (error) {
        console.error("Failed to load admin order details:", error);
        alert("Failed to load order details");
        navigate("/admin/orders");
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="aod-page">
        <AdminNavbar />
        <div className="aod-container">
          <div className="aod-loading">
            <div className="aod-spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const status = getStatusConfig(order.status);

  return (
    <div className="aod-page">
      <AdminNavbar />

      <div className="aod-container">
        <button
          className="aod-back-btn"
          onClick={() => navigate("/admin/orders")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Orders
        </button>

        <div className="aod-header">
          <div>
            <h1>Order #{String(order._id).slice(-8).toUpperCase()}</h1>
            <p>
              {order.createdAt
                ? new Date(order.createdAt).toLocaleString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-"}
            </p>
          </div>
          <div className="aod-header-right">
            <span
              className="aod-status"
              style={{ color: status.color, background: status.bg }}
            >
              {status.label}
            </span>
            <strong>
              BND {Number(order.total || order.totalPrice || 0).toFixed(2)}
            </strong>
          </div>
        </div>

        <div className="aod-grid">
          <section className="aod-card">
            <h2>Items</h2>
            <div className="aod-items">
              {(order.items || []).map((item, index) => {
                const imageUrl = getItemImageUrl(item);

                return (
                  <div
                    key={`${item._id || item.product || index}`}
                    className="aod-item"
                  >
                    <div className="aod-item-image">
                      {imageUrl ? (
                        <img src={imageUrl} alt={item.name} />
                      ) : (
                        <div className="aod-no-image">No Image</div>
                      )}
                    </div>
                    <div className="aod-item-info">
                      <h3>{item.name}</h3>
                      {item.variantName ? (
                        <p>Variation: {item.variantName}</p>
                      ) : null}
                      <p>Qty: {item.quantity}</p>
                    </div>
                    <div className="aod-item-price">
                      <span>BND {Number(item.price || 0).toFixed(2)} each</span>
                      <strong>
                        BND{" "}
                        {Number(
                          (item.price || 0) * (item.quantity || 0),
                        ).toFixed(2)}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <aside className="aod-card">
            <h2>Order Info</h2>
            <div className="aod-info-row">
              <span>Buyer</span>
              <span>{order.user?.username || "Unknown"}</span>
            </div>
            <div className="aod-info-row">
              <span>Email</span>
              <span>{order.user?.email || "-"}</span>
            </div>
            <div className="aod-info-row">
              <span>Payment</span>
              <span>
                {String(order.paymentMethod || "-").replace("_", " ")}
              </span>
            </div>
            <div className="aod-info-row">
              <span>Items</span>
              <span>{order.itemCount || order.items?.length || 0}</span>
            </div>
            <div className="aod-info-row total">
              <span>Total</span>
              <strong>
                BND {Number(order.total || order.totalPrice || 0).toFixed(2)}
              </strong>
            </div>

            <h3>Shipping Address</h3>
            <p className="aod-address">
              {order.shippingAddress?.receiverName || ""}
              <br />
              {order.shippingAddress?.phone || ""}
              <br />
              {order.shippingAddress?.street || ""}
              <br />
              {order.shippingAddress?.city || ""},{" "}
              {order.shippingAddress?.state || ""}{" "}
              {order.shippingAddress?.postalCode || ""}
              <br />
              {order.shippingAddress?.country || ""}
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
